import { useState, useEffect, useMemo } from 'react'
import type { Result, PanelDictionaryEntry, UserRole } from '@alpha-edge/shared'
import { H_AND_S_HOLD_REASONS, roleSatisfies } from '@alpha-edge/shared'
import type { HoldReason } from '@alpha-edge/shared'
import {
  Modal,
  ConfirmDialog,
  StatusBadge,
  PrintLabelButton,
  btnPrimary,
  btnSecondary,
  colors,
  input,
  label as labelStyle,
} from '../../components/ui'
import { Link } from 'react-router-dom'
import { api, ApiError } from '../../services/api'
import { useAuth } from '../../hooks/use-auth'

interface RejectionReason {
  readonly code: string
  readonly label: string
  readonly requiresNote?: boolean
}

interface ResultReviewModalProps {
  readonly result: Result | null
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly onUpdated: (updated: Result) => void
}

function formatAge(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime()
  const min = Math.floor(ms / 60000)
  if (min < 60) return `${min}m`
  const hrs = Math.floor(min / 60)
  if (hrs < 24) return `${hrs}h ${min % 60}m`
  const days = Math.floor(hrs / 24)
  return `${days}d ${hrs % 24}h`
}

function DetailRow({ labelText, children }: { readonly labelText: string; readonly children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        padding: '0.4rem 0',
        borderBottom: `1px solid ${colors.border}`,
        fontSize: '0.9375rem',
      }}
    >
      <span style={{ width: '40%', color: colors.textMuted, fontWeight: 500 }}>{labelText}</span>
      <span style={{ flex: 1, color: colors.text }}>{children}</span>
    </div>
  )
}

export function ResultReviewModal({ result, isOpen, onClose, onUpdated }: ResultReviewModalProps) {
  const [note, setNote] = useState('')
  const [criticalNote, setCriticalNote] = useState('')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [rejectionReasons, setRejectionReasons] = useState<readonly RejectionReason[]>([])
  const [showCancelForm, setShowCancelForm] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelReasonCode, setCancelReasonCode] = useState<string>('')
  const [cancelNote, setCancelNote] = useState<string>('')

  // Hold form state
  const [showHoldForm, setShowHoldForm] = useState(false)
  const [holdReasonCode, setHoldReasonCode] = useState<string>('')
  const [holdReasonNote, setHoldReasonNote] = useState<string>('')
  const [showClearHoldForm, setShowClearHoldForm] = useState(false)
  const [clearHoldNote, setClearHoldNote] = useState<string>('')

  useEffect(() => {
    if (result) {
      setNote(result.note ?? '')
      setCriticalNote(result.criticalNote ?? '')
      setError(null)
      setErrorCode(null)
      setShowCancelForm(false)
      setShowCancelConfirm(false)
      setCancelReasonCode('')
      setCancelNote('')
      setShowHoldForm(false)
      setHoldReasonCode('')
      setHoldReasonNote('')
      setShowClearHoldForm(false)
      setClearHoldNote('')
    }
  }, [result])

  // Phase 5.7.3: pull the org-wide panel-dictionary rejection reasons
  // so cancel/reject uses a structured picker instead of window.prompt.
  // Loads once per modal open; silently falls back to a free-text field
  // when no dictionary is configured.
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    api
      .get<readonly PanelDictionaryEntry[]>('/panel-dictionary?limit=50')
      .then((res) => {
        if (cancelled) return
        const merged = new Map<string, RejectionReason>()
        for (const entry of res.data ?? []) {
          for (const raw of entry.rejectionReasons ?? []) {
            const r = raw as unknown as RejectionReason
            if (r && typeof r.code === 'string' && typeof r.label === 'string' && !merged.has(r.code)) {
              merged.set(r.code, r)
            }
          }
        }
        setRejectionReasons(Array.from(merged.values()))
      })
      .catch(() => {
        // non-fatal -- empty list just falls back to free-text reason
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  // Merge H&S hold reasons (prepended) with org panel-dictionary reasons.
  // Panel-dictionary entries win on code collision so orgs can override defaults.
  const holdReasons = useMemo<readonly HoldReason[]>(() => {
    const merged = new Map<string, HoldReason>()
    for (const r of H_AND_S_HOLD_REASONS) merged.set(r.code, r)
    for (const r of rejectionReasons) merged.set(r.code, r as HoldReason)
    return Array.from(merged.values())
  }, [rejectionReasons])

  const { user } = useAuth()

  if (!result) return null

  const isHeld = !!result.isOnHold
  const canVerify = result.status === 'resulted' && !isHeld
  const canSubmitForRelease = result.status === 'verified' && !isHeld
  const canRelease = result.status === 'pending_release' && !isHeld
  const canVerifyAndSubmit = result.status === 'resulted' && !isHeld
  const canCancel = result.status !== 'canceled'
  const canPlaceHold = !isHeld && result.status !== 'released' && result.status !== 'canceled'
  const canClearHoldRole = !!user && roleSatisfies(user.role as UserRole, ['admin', 'customer_admin'])
  const canClearHold = isHeld && canClearHoldRole

  function clearError() {
    setError(null)
    setErrorCode(null)
  }

  // Surface an error and, for an ApiError, its machine-readable code, so
  // role-aware help (e.g. the two-person QA banner) shows only for the
  // matching error and never lingers under an unrelated later error.
  function applyError(err: unknown, fallback: string) {
    setError(err instanceof ApiError ? err.message : fallback)
    setErrorCode(err instanceof ApiError ? (err.code ?? null) : null)
  }

  async function callAction(status: 'verified' | 'pending_release' | 'released' | 'canceled') {
    if (!result) return
    setActionLoading(status)
    clearError()
    try {
      const body: Record<string, unknown> = { status }
      if (note !== (result.note ?? '')) body.note = note
      if (status === 'canceled') {
        const cancelReason = buildCancelReason()
        if (!cancelReason) {
          setError('A cancellation reason is required.')
          setActionLoading(null)
          return
        }
        body.cancelReason = cancelReason
      }
      if (result.isCritical && status === 'released' && criticalNote.trim().length === 0) {
        setError('A critical note is required to release a critical result.')
        setActionLoading(null)
        return
      }
      if (result.isCritical && criticalNote !== (result.criticalNote ?? '')) {
        body.criticalNote = criticalNote
      }
      const res = await api.put<Result>(`/results/${result.id}`, body)
      if (res.data) onUpdated({ ...result, ...res.data })
      onClose()
    } catch (err) {
      applyError(err, 'Action failed')
    } finally {
      setActionLoading(null)
    }
  }

  function buildCancelReason(): string | null {
    if (rejectionReasons.length === 0) {
      return cancelNote.trim() || null
    }
    const picked = rejectionReasons.find((r) => r.code === cancelReasonCode)
    if (!picked) return null
    if (picked.requiresNote && cancelNote.trim().length === 0) return null
    return cancelNote.trim() ? `${picked.label} -- ${cancelNote.trim()}` : picked.label
  }

  // Phase 5.8.4: combo flow now stops at pending_release because the
  // QA gate requires a different user to release. The verifier can
  // verify+submit-for-release in a single click; a second reviewer
  // takes it from there using the dedicated Release button.
  async function verifyAndSubmitForRelease() {
    if (!result) return
    setActionLoading('both')
    clearError()
    try {
      const verifyBody: Record<string, unknown> = { status: 'verified' }
      if (note !== (result.note ?? '')) verifyBody.note = note
      await api.put<Result>(`/results/${result.id}`, verifyBody)
      const submitBody: Record<string, unknown> = { status: 'pending_release' }
      const submitted = await api.put<Result>(`/results/${result.id}`, submitBody)
      if (submitted.data) onUpdated({ ...result, ...submitted.data })
      onClose()
    } catch (err) {
      applyError(err, 'Failed to submit for release')
    } finally {
      setActionLoading(null)
    }
  }

  async function handlePlaceHold() {
    if (!result) return
    clearError()
    const picked = holdReasons.find((r) => r.code === holdReasonCode)
    if (!picked) {
      setError('Select a hold reason.')
      return
    }
    if (picked.requiresNote && holdReasonNote.trim().length === 0) {
      setError('A note is required for this hold reason.')
      return
    }
    setActionLoading('hold')
    try {
      const res = await api.post<Result>(`/results/${result.id}/hold`, {
        reasonCode: picked.code,
        reasonNote: holdReasonNote.trim() || undefined,
      })
      if (res.data) onUpdated({ ...result, ...res.data })
      onClose()
    } catch (err) {
      applyError(err, 'Failed to place hold')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleClearHold() {
    if (!result) return
    setActionLoading('clear-hold')
    clearError()
    try {
      const res = await api.post<Result>(`/results/${result.id}/clear-hold`, {
        note: clearHoldNote.trim() || undefined,
      })
      if (res.data) onUpdated({ ...result, ...res.data })
      onClose()
    } catch (err) {
      applyError(err, 'Failed to clear hold')
    } finally {
      setActionLoading(null)
    }
  }

  const title = `Result: ${result.barcode || result.id.slice(0, 8)}`

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} width="600px">
      <div>
        {result.isOnHold && (
          <div
            style={{
              padding: '0.6rem 0.75rem',
              marginBottom: '1rem',
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '4px',
              color: '#92400e',
              fontSize: '0.9375rem',
              fontWeight: 500,
            }}
          >
            ON HOLD
            {result.holdReasonCode &&
              (() => {
                const reason = holdReasons.find((r) => r.code === result.holdReasonCode)
                return ` — ${reason?.label ?? result.holdReasonCode}`
              })()}
            {result.heldAt && ` (held ${formatAge(result.heldAt)} ago)`}
            {result.holdReasonNote && (
              <div style={{ marginTop: '0.25rem', fontSize: '0.875rem', fontWeight: 400, color: '#78350f' }}>
                Note: {result.holdReasonNote}
              </div>
            )}
          </div>
        )}

        {result.isCritical && (
          <div
            style={{
              padding: '0.6rem 0.75rem',
              marginBottom: '1rem',
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '4px',
              color: '#dc2626',
              fontSize: '0.9375rem',
              fontWeight: 500,
            }}
          >
            CRITICAL RESULT
            {result.criticalFlaggedAt && ` — flagged ${formatAge(result.criticalFlaggedAt)} ago`}
          </div>
        )}

        <DetailRow labelText="Test Type">{result.testType}</DetailRow>
        <DetailRow labelText="Status">
          <StatusBadge status={result.status} />
        </DetailRow>
        <DetailRow labelText="Result Value">{result.resultValue ?? '--'}</DetailRow>
        <DetailRow labelText="Barcode">{result.barcode ?? '--'}</DetailRow>
        {result.source === 'external' && (
          <>
            <DetailRow labelText="Source">
              <StatusBadge status="info" label="External / Send-out" />
            </DetailRow>
            <DetailRow labelText="External Lab">{result.externalLabName ?? '--'}</DetailRow>
            {result.externalAccessionId && (
              <DetailRow labelText="External Ref #">{result.externalAccessionId}</DetailRow>
            )}
          </>
        )}
        <DetailRow labelText="Created">{new Date(result.createdAt).toLocaleString()}</DetailRow>
        <DetailRow labelText="Age">{formatAge(result.createdAt)}</DetailRow>
        {result.resultTimestamp && (
          <DetailRow labelText="Result Time">{new Date(result.resultTimestamp).toLocaleString()}</DetailRow>
        )}
        {result.verifiedAt && (
          <DetailRow labelText="Verified">{new Date(result.verifiedAt).toLocaleString()}</DetailRow>
        )}
        {result.releasedAt && (
          <DetailRow labelText="Released">{new Date(result.releasedAt).toLocaleString()}</DetailRow>
        )}
        {result.isFalsePositive && (
          <DetailRow labelText="Flag">
            <StatusBadge status="warning" label="FALSE POSITIVE" />
          </DetailRow>
        )}

        <div style={{ marginTop: '1rem' }}>
          <label style={labelStyle}>Note</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            disabled={result.status === 'released' || result.status === 'canceled'}
            style={{ ...input, height: 'auto', resize: 'vertical', fontFamily: 'inherit' }}
            placeholder="Additional notes about this result..."
          />
        </div>

        {result.isCritical && (
          <div style={{ marginTop: '0.75rem' }}>
            <label style={labelStyle}>Critical Note (required to release)</label>
            <textarea
              value={criticalNote}
              onChange={(e) => setCriticalNote(e.target.value)}
              rows={2}
              disabled={result.status === 'released' || result.status === 'canceled'}
              style={{ ...input, height: 'auto', resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Document critical result communication (provider notified, acknowledgment, etc.)"
            />
          </div>
        )}

        {showHoldForm && canPlaceHold && (
          <div
            style={{
              marginTop: '0.75rem',
              padding: '0.75rem',
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '3px',
            }}
          >
            <label style={labelStyle}>Hold Reason</label>
            <select
              value={holdReasonCode}
              onChange={(e) => setHoldReasonCode(e.target.value)}
              style={{ ...input, marginBottom: '0.5rem' }}
            >
              <option value="">-- Select a reason --</option>
              {holdReasons.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                  {r.requiresNote ? ' (note required)' : ''}
                </option>
              ))}
            </select>
            <textarea
              value={holdReasonNote}
              onChange={(e) => setHoldReasonNote(e.target.value)}
              rows={2}
              placeholder="Additional detail (required for some reasons)"
              style={{ ...input, height: 'auto', resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => {
                  setShowHoldForm(false)
                  setHoldReasonCode('')
                  setHoldReasonNote('')
                  setError(null)
                }}
                style={btnSecondary}
                disabled={actionLoading !== null}
              >
                Keep Active
              </button>
              <button
                type="button"
                onClick={handlePlaceHold}
                style={{ ...btnSecondary, background: '#d97706', color: '#fff' }}
                disabled={actionLoading !== null}
              >
                {actionLoading === 'hold' ? 'Placing hold...' : 'Confirm Hold'}
              </button>
            </div>
          </div>
        )}

        {showClearHoldForm && canClearHold && (
          <div
            style={{
              marginTop: '0.75rem',
              padding: '0.75rem',
              background: colors.bgMuted,
              border: `1px solid ${colors.border}`,
              borderRadius: '3px',
            }}
          >
            <label style={labelStyle}>Clear-Hold Note (optional)</label>
            <textarea
              value={clearHoldNote}
              onChange={(e) => setClearHoldNote(e.target.value)}
              rows={2}
              placeholder="Reason for clearing the hold (optional)"
              style={{ ...input, height: 'auto', resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => {
                  setShowClearHoldForm(false)
                  setClearHoldNote('')
                  setError(null)
                }}
                style={btnSecondary}
                disabled={actionLoading !== null}
              >
                Keep on Hold
              </button>
              <button type="button" onClick={handleClearHold} style={btnPrimary} disabled={actionLoading !== null}>
                {actionLoading === 'clear-hold' ? 'Clearing...' : 'Confirm Clear'}
              </button>
            </div>
          </div>
        )}

        {showCancelForm && canCancel && (
          <div
            style={{
              marginTop: '0.75rem',
              padding: '0.75rem',
              background: colors.bgError,
              border: `1px solid ${colors.borderError}`,
              borderRadius: '3px',
            }}
          >
            <label style={labelStyle}>Cancellation Reason</label>
            {rejectionReasons.length > 0 ? (
              <select
                value={cancelReasonCode}
                onChange={(e) => setCancelReasonCode(e.target.value)}
                style={{ ...input, marginBottom: '0.5rem' }}
              >
                <option value="">-- Select a reason --</option>
                {rejectionReasons.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                    {r.requiresNote ? ' (note required)' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <div style={{ fontSize: '0.8125rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
                No panel-dictionary reasons configured; enter a free-text reason below.
              </div>
            )}
            <textarea
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              rows={2}
              placeholder={
                rejectionReasons.length > 0
                  ? 'Additional detail (required for some reasons)'
                  : 'Cancellation reason (required)'
              }
              style={{ ...input, height: 'auto', resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={() => {
                  setShowCancelForm(false)
                  setCancelReasonCode('')
                  setCancelNote('')
                  setError(null)
                }}
                style={btnSecondary}
                disabled={actionLoading !== null}
              >
                Keep Result
              </button>
              <button
                type="button"
                onClick={() => {
                  if (result.status === 'released') {
                    setShowCancelConfirm(true)
                  } else {
                    callAction('canceled')
                  }
                }}
                style={{ ...btnSecondary, background: colors.danger, color: '#fff' }}
                disabled={actionLoading !== null}
              >
                {actionLoading === 'canceled' ? 'Canceling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              marginTop: '0.75rem',
              color: colors.danger,
              padding: '0.5rem 0.75rem',
              background: colors.bgError,
              border: `1px solid ${colors.borderError}`,
              borderRadius: '3px',
              fontSize: '0.875rem',
            }}
          >
            <div>{error}</div>
            {errorCode === 'two_person_release_required' && (
              <div style={{ marginTop: '0.5rem' }}>
                This lab requires a different user to release than the one who verified the result.{' '}
                {user && roleSatisfies(user.role as UserRole, ['admin']) ? (
                  <Link to="/settings/org" style={{ color: colors.primary, textDecoration: 'underline' }}>
                    Disable the two-person release requirement in Organization Settings →
                  </Link>
                ) : (
                  <span>
                    Ask an administrator to release this result, or to adjust this requirement in Organization Settings.
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            marginTop: '1.25rem',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <button type="button" onClick={onClose} style={btnSecondary} disabled={actionLoading !== null}>
            Close
          </button>
          {result.orderId && (
            <PrintLabelButton
              orderId={result.orderId}
              onError={(e) => {
                setError(e.message)
                setErrorCode(null)
              }}
            />
          )}
          {canCancel && !showCancelForm && (
            <button
              type="button"
              onClick={() => setShowCancelForm(true)}
              style={{ ...btnSecondary, background: colors.danger, color: '#fff' }}
              disabled={actionLoading !== null}
            >
              Cancel Result
            </button>
          )}
          {canPlaceHold && !showHoldForm && (
            <button
              type="button"
              onClick={() => setShowHoldForm(true)}
              style={{ ...btnSecondary, background: '#d97706', color: '#fff' }}
              disabled={actionLoading !== null}
            >
              Place Hold
            </button>
          )}
          {canClearHold && !showClearHoldForm && (
            <button
              type="button"
              onClick={() => setShowClearHoldForm(true)}
              style={btnPrimary}
              disabled={actionLoading !== null}
            >
              Clear Hold
            </button>
          )}
          {canVerify && (
            <button
              type="button"
              onClick={() => callAction('verified')}
              style={btnSecondary}
              disabled={actionLoading !== null}
            >
              {actionLoading === 'verified' ? '...' : 'Verify'}
            </button>
          )}
          {canVerifyAndSubmit && (
            <button
              type="button"
              onClick={verifyAndSubmitForRelease}
              style={btnPrimary}
              disabled={actionLoading !== null}
            >
              {actionLoading === 'both' ? 'Working...' : 'Verify + Submit for Release'}
            </button>
          )}
          {canSubmitForRelease && (
            <button
              type="button"
              onClick={() => callAction('pending_release')}
              style={btnPrimary}
              disabled={actionLoading !== null}
            >
              {actionLoading === 'pending_release' ? '...' : 'Submit for Release'}
            </button>
          )}
          {canRelease && (
            <button
              type="button"
              onClick={() => callAction('released')}
              style={btnPrimary}
              disabled={actionLoading !== null}
              title="Releasing requires a different user from the verifier (two-person QA rule). An admin can change this in Organization Settings."
            >
              {actionLoading === 'released' ? 'Releasing...' : 'Release'}
            </button>
          )}
          {result.status === 'canceled' && (
            <button
              type="button"
              onClick={() => callAction('released')}
              style={btnPrimary}
              disabled={actionLoading !== null}
            >
              {actionLoading === 'released' ? 'Restoring...' : 'Restore to Released'}
            </button>
          )}
        </div>
        <ConfirmDialog
          isOpen={showCancelConfirm}
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={() => {
            setShowCancelConfirm(false)
            callAction('canceled')
          }}
          title="Cancel Released Result?"
          message={
            'This result has already been released and may have been sent to external systems. ' +
            'Canceling will invalidate the original report and generate a cancellation notice. ' +
            'Are you sure you want to proceed?'
          }
          confirmLabel="Cancel This Result"
          cancelLabel="Go Back"
          variant="danger"
          loading={actionLoading === 'canceled'}
        />
        {isHeld && !canClearHold && (
          <div style={{ textAlign: 'right', marginTop: '0.5rem', fontSize: '0.8125rem', color: colors.textMuted }}>
            Clear the hold before changing status.
          </div>
        )}
      </div>
    </Modal>
  )
}
